import React from 'react';
import styled, { css } from 'styled-components';
import {
  Icon,
  isDarkThemeMixin,
  Link,
  styleMixinFocusOutline,
  styleMixinInvertedFocusOutline,
  Text,
} from '@amzn/storm-ui';
import { angleInlineStart } from '@amzn/storm-ui-icons';
import { getClickEventHandler } from 'src/commons/utils';
import type { Breadcrumb as BreadcrumbType } from 'src/app/slice';
import { useLinkUpdater } from 'src/commons/utils/linkUpdater';

const StyledLink = styled(Link)`
  display: flex;
  border-radius: 0;
  padding: ${({ theme }) =>
    `${theme.spacing.xxsmall.mobile} ${theme.spacing.xsmall.mobile}`};
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxxsmall.mobile};
  background-color: ${({ theme }) => theme.surface.container.offset.inverted};

  ${isDarkThemeMixin(css`
    background-color: ${({ theme }) => theme.surface.subtleOffset};
  `)};

  && {
    color: ${({ theme }) => theme.text.color.inverted};

    ${isDarkThemeMixin(css`
      color: ${({ theme }) => theme.text.color.default};
    `)};
  }

  /* Breadcrumb is not a standard blue link: keep the inverted color and no
     underline on hover for the label and the chevron icon. */
  &&:hover,
  &&:hover * {
    color: ${({ theme }) => theme.text.color.inverted};
    text-decoration: none;

    ${isDarkThemeMixin(css`
      color: ${({ theme }) => theme.text.color.default};
    `)};
  }

  :focus-visible {
    ${styleMixinInvertedFocusOutline};

    ${isDarkThemeMixin(css`
      ${styleMixinFocusOutline};
    `)};

    outline-offset: -2px;
  }
`;

const StyledText = styled(Text)`
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  & {
    color: ${({ theme }) => theme.text.color.inverted};
    ${isDarkThemeMixin(css`
      color: ${({ theme }) => theme.text.color.default};
    `)};
  }

  &:hover {
    text-decoration: none;
  }
`;

interface MobileBreadcrumbProps {
  breadcrumb: BreadcrumbType;
}

const MobileBreadcrumb: React.VFC<MobileBreadcrumbProps> = ({ breadcrumb }) => {
  const transformUrl = useLinkUpdater();
  const eventHandler = getClickEventHandler(breadcrumb);
  const { label, url, objectType } = breadcrumb;
  const resolvedUrl = url && transformUrl ? transformUrl(url) : url;

  return (
    <StyledLink href={resolvedUrl} onClick={eventHandler}>
      <Icon type={angleInlineStart} size="xsmall" />
      <StyledText renderAs="span">
        {objectType ? `${objectType}: ${label}` : label}
      </StyledText>
    </StyledLink>
  );
};

export default MobileBreadcrumb;
